import { $enum } from "ts-enum-util";

import { ConsumptionGranularity, type Meter } from "@eisox/api/core-api";
import { Select, ToggleGroup } from "@eisox/design-system";

import { DayRangePicker } from "~/components/day-range-picker";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

import {
  CONSUMPTION_TABS,
  ENERGIES,
  GRANULARITIES,
  GRANULARITY_ORDER,
  PERIOD_PRESETS,
  SPAN_COUNTS,
} from "../consumption.constant";
import type { ConsumptionSearch } from "../helpers/consumption-search.helper";
import { meterGroups, meterLinks } from "../helpers/meter.helper";
import { granularityChoice } from "../helpers/view-change.helper";
import { ConsumptionTab, PeriodPreset, type ChartView } from "../types/consumption.type";

const NAME = "ConsumptionToolbar";
const SEARCH_INPUT_THRESHOLD = 10;
const SUB_METER_INDENT_PX = 12.5;
const CUSTOM_PERIOD = "custom";

interface ConsumptionToolbarProps {
  meters: Meter[];
  meter: Meter;
  tab: ConsumptionTab;
  view: ChartView;
  today: string;
  hasHierarchy: boolean;
  onMeterChange: (meterId: string) => void;
  onSearchChange: (search: Partial<ConsumptionSearch>) => void;
}

const ConsumptionToolbar: React.FC<ConsumptionToolbarProps> = ({
  meters,
  meter,
  tab,
  view,
  today,
  hasHierarchy,
  onMeterChange,
  onSearchChange,
}) => {
  const { building, graph } = useBuildingContext(NAME);
  const { period } = view;
  const groups = meterGroups(meters, tab === ConsumptionTab.HIERARCHY);

  const periodOptions = [
    ...Object.values(PeriodPreset).map(preset => {
      const { amount, unit } = PERIOD_PRESETS[preset].span;
      return { value: preset, label: SPAN_COUNTS[unit](amount) };
    }),
    { value: CUSTOM_PERIOD, label: m.personalized() },
  ];

  const handlePeriodChange = (value: string) => {
    if (value === CUSTOM_PERIOD) onSearchChange({ preset: undefined, range: period.range });
    if ($enum(PeriodPreset).isValue(value)) onSearchChange({ preset: value, range: undefined, granularity: undefined });
  };

  return (
    <div className="gap-x-5 gap-y-2.5 @container flex flex-wrap items-center">
      <div className="gap-2.5 @max-3xl:w-full flex items-center">
        <Select.Root multiple={false} value={meter.id} onValueChange={onMeterChange}>
          <Select.Trigger className="min-w-0 @3xl:w-72 @3xl:flex-none flex-1" aria-label={m.consumption_meter()}>
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            {meters.length > SEARCH_INPUT_THRESHOLD && <Select.Input />}
            <Select.Viewport>
              {groups.map(({ energyType, nodes }) => {
                const energy = energyType && ENERGIES[energyType];
                return (
                  <Select.Group
                    key={energyType ?? "none"}
                    className="not-first:mt-1.25 not-first:pt-1.25 not-first:border-t not-first:border-primary-gray **:[[cmdk-group-heading]]:text-sm **:[[cmdk-group-heading]]:text-primary-dark-gray"
                    heading={
                      energy ? (
                        <span className="gap-1.25 flex items-center">
                          <energy.Icon />
                          {energy.label()}
                        </span>
                      ) : (
                        m.none()
                      )
                    }
                  >
                    {nodes.map(({ meter: node, depth }) => {
                      const links = meterLinks(graph, node.id);
                      return (
                        <Select.Item key={node.id} value={node.id}>
                          <span className="min-w-0 flex flex-col" style={{ paddingLeft: depth * SUB_METER_INDENT_PX }}>
                            {node.name}
                            {links && <span className="text-sm text-primary-dark-gray">{links}</span>}
                          </span>
                          <Select.ItemText>{node.name}</Select.ItemText>
                        </Select.Item>
                      );
                    })}
                  </Select.Group>
                );
              })}
              <Select.Empty>{m.no_result()}</Select.Empty>
            </Select.Viewport>
          </Select.Content>
        </Select.Root>
        <ToggleGroup.Root
          value={tab}
          onValueChange={value => {
            if ($enum(ConsumptionTab).isValue(value)) onSearchChange({ tab: value });
          }}
        >
          {CONSUMPTION_TABS.map(({ tab: value, Icon, label }) => (
            <ToggleGroup.Item key={value} value={value} disabled={value === ConsumptionTab.HIERARCHY && !hasHierarchy}>
              <Icon />
              <span className="@max-lg:sr-only">{label()}</span>
            </ToggleGroup.Item>
          ))}
        </ToggleGroup.Root>
      </div>
      <div className="gap-2.5 @max-3xl:w-full flex flex-wrap items-center">
        <ToggleGroup.Root
          className="@max-3xl:hidden"
          value={period.preset ?? CUSTOM_PERIOD}
          onValueChange={handlePeriodChange}
        >
          {periodOptions.map(({ value, label }) => (
            <ToggleGroup.Item key={value} value={value}>
              {label}
            </ToggleGroup.Item>
          ))}
        </ToggleGroup.Root>
        <Select.Root multiple={false} value={period.preset ?? CUSTOM_PERIOD} onValueChange={handlePeriodChange}>
          <Select.Trigger className="@3xl:hidden flex-1" aria-label={m.period()}>
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            <Select.Viewport>
              {periodOptions.map(({ value, label }) => (
                <Select.Item key={value} value={value}>
                  {label}
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Root>
        <Select.Root
          multiple={false}
          value={period.granularity}
          onValueChange={value => {
            if ($enum(ConsumptionGranularity).isValue(value))
              onSearchChange(granularityChoice(view, value, building, today));
          }}
        >
          <Select.Trigger className="@3xl:w-36 @3xl:flex-none flex-1" aria-label={m.consumption_granularity()}>
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            <Select.Viewport>
              {GRANULARITY_ORDER.map(granularity => (
                <Select.Item key={granularity} value={granularity}>
                  {GRANULARITIES[granularity].label()}
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Root>
        {!period.preset && (
          <DayRangePicker
            value={period.range}
            onValueChange={range => onSearchChange({ range })}
            max={today}
            unit={GRANULARITIES[period.granularity].calendarUnit}
            className="@3xl:w-fit"
          />
        )}
      </div>
    </div>
  );
};

ConsumptionToolbar.displayName = NAME;

export { ConsumptionToolbar };
